/**
 * Utility for mapping CPM‑Object‑Store entry proxy types to JSON‑Schema 7
 * definitions.
 *
 * This module contains a small lookup map that returns a JSON‑Schema object
 * representing the type of a value that can be stored in the Object‑Store.
 * The mapping is derived from the generated enum
 * {@link ECpmCObjectStoreEntryProxyTypeApiEnum}.
 *
 * @module ObjectStoreEntryUtil
 */

import {JSONSchema7, JSONSchema7TypeName} from 'json-schema';
import {ECpmCObjectStoreEntryProxyTypeApiEnum} from '../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-object-store-entry-proxy.api-enums';
import {CpmCObjectStoreEntryProxyApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-object-store-entry-proxy.api-model';
import {ISubmitRequestMessages} from '../../../sdk/src/components/ibase/ibase.component';
import {ActionJobsUtils} from './action-jobs.utils';
import {JsonSchemaUtils} from '../../../sdk/src/utils/json-schema.utils';
import {isArray} from 'lodash';

/**
 * Individual JSON‑Schema definitions used by the lookup map.
 *
 * They are exported so other parts of the codebase can reuse them if needed.
 */
export const objectStoreEntryString: JSONSchema7 = {type: 'string'};
export const objectStoreEntryInteger: JSONSchema7 = {type: 'integer'};
export const objectStoreEntryFloat: JSONSchema7 = {type: 'number'};
export const objectStoreEntryBoolean: JSONSchema7 = {type: 'boolean'};
export const objectStoreEntryStringArray: JSONSchema7 = {
    type: 'array',
    items: {type: 'string'}
};
export const objectStoreEntryIntegerArray: JSONSchema7 = {
    type: 'array',
    items: {type: 'integer'}
};
export const objectStoreEntryFloatArray: JSONSchema7 = {
    type: 'array',
    items: {type: 'number'}
};
export const objectStoreEntryBooleanArray: JSONSchema7 = {
    type: 'array',
    items: {type: 'boolean'}
};

/**
 * Mapping between enum values and their corresponding JSON‑Schema objects.
 *
 * The `as const` assertion ensures that the map’s keys are the exact enum
 * members, so TypeScript can infer the type of the values.
 */
const schemaMap: Record<ECpmCObjectStoreEntryProxyTypeApiEnum, JSONSchema7 | undefined> = {
    [ECpmCObjectStoreEntryProxyTypeApiEnum.STRING]: objectStoreEntryString,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.INTEGER]: objectStoreEntryInteger,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.FLOAT]: objectStoreEntryFloat,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.BOOLEAN]: objectStoreEntryBoolean,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.STRING_ARRAY]: objectStoreEntryStringArray,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.INTEGER_ARRAY]: objectStoreEntryIntegerArray,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.FLOAT_ARRAY]: objectStoreEntryFloatArray,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.BOOLEAN_ARRAY]: objectStoreEntryBooleanArray,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT]: undefined,
    [ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT_ARRAY]: undefined
};

/**
 * Utility class that exposes a single public helper.
 *
 * The method `getSchemaForVtype` performs a map lookup and returns the
 * corresponding JSON‑Schema, or `undefined` if the enum value is unknown.
 */
export abstract class ObjectStoreEntryUtil {
    /**
     * Returns a JSON‑Schema7 object that represents the type of value
     * associated with the provided proxy type enum.
     *
     * @param vtype - The proxy type enum value.
     * @returns The matching JSON‑Schema7 object, or `undefined` if no match
     *          exists.
     *
     * @example
     * ```ts
     * const schema = ObjectStoreEntryUtil.getSchemaForVtype(
     *   ECpmCObjectStoreEntryProxyTypeApiEnum.STRING
     * );
     * // schema === { type: 'string' }
     * ```
     */
    public static async getSchemaForVtype(
        vtype: ECpmCObjectStoreEntryProxyTypeApiEnum,
        schemaKey?: string
    ): Promise<JSONSchema7 | undefined> {
        if (
            vtype == ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT ||
            vtype == ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT_ARRAY
        ) {
            if (!schemaKey) return undefined;

            const objectSchema = await ActionJobsUtils.getSchema(schemaKey);
            if (objectSchema && vtype == ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT_ARRAY) {
                return {
                    $id: objectSchema.$id,
                    type: 'array',
                    items: objectSchema,
                    definitions: objectSchema.definitions
                };
            }

            return objectSchema;
        }

        return schemaMap[vtype];
    }

    // ===============================================================
    // ===============================================================

    /**
     * Resolve object store entry vType & schemaKey from a plugin function
     * @param propertySchema Schema of the property
     * @param rootSchema Root schema for the plugin function input or output
     * @returns Resolved vType and schemaKey
     */
    public static resolveTypeFromPluginFunctionJsonSchemaProperty(
        propertySchema: JSONSchema7,
        rootSchema: JSONSchema7
    ): {vtype: ECpmCObjectStoreEntryProxyTypeApiEnum; schemaKey?: string} {
        if (propertySchema.anyOf) {
            if (
                propertySchema.anyOf.length == 2 &&
                propertySchema.anyOf.some((x) => (x as JSONSchema7).type == 'null')
            ) {
                const nonNullSchema = propertySchema.anyOf.find(
                    (x) => (x as JSONSchema7).type != 'null'
                ) as JSONSchema7;
                return ObjectStoreEntryUtil.resolveTypeFromPluginFunctionJsonSchemaProperty(nonNullSchema, rootSchema);
            } else throw new Error('Unsupported anyOf with more than 2 types');
        }

        if (propertySchema.$ref) {
            const resolvedRef = JsonSchemaUtils.resolveRef(propertySchema.$ref, rootSchema);
            if (!resolvedRef) throw new Error(`Could not resolve JsonSchema ref '${propertySchema.$ref}'`);

            propertySchema = resolvedRef;
        }

        let propertyTypes: JSONSchema7TypeName[] = [];
        if (isArray(propertySchema.type)) propertyTypes = propertySchema.type;
        else if (propertySchema.type) propertyTypes = [propertySchema.type];

        const propertySelectedType = propertyTypes.filter((x) => x != 'null')[0];
        const isPropertyAnArray = propertySelectedType == 'array';

        let vType = isPropertyAnArray
            ? ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT_ARRAY
            : ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT;

        let arrayItemsProperty: JSONSchema7 | undefined = undefined;
        if (propertySchema.items) {
            if (isArray(propertySchema.items) || propertySchema!.items === true) {
                throw new Error('Unsupported multiple type array');
            }

            arrayItemsProperty = propertySchema.items!;

            if (arrayItemsProperty.$ref) {
                const resolvedRef = JsonSchemaUtils.resolveRef(arrayItemsProperty.$ref, rootSchema);
                if (!resolvedRef) throw new Error(`Could not resolve JsonSchema ref '${arrayItemsProperty.$ref}'`);

                arrayItemsProperty = resolvedRef;
            }
        }

        if (isPropertyAnArray) {
            if (!arrayItemsProperty) throw new Error('Expected array items property for an array type');

            let arrayItemTypes: JSONSchema7TypeName[] = [];
            if (isArray(arrayItemsProperty.type)) arrayItemTypes = arrayItemsProperty.type;
            else if (arrayItemsProperty.type) arrayItemTypes = [arrayItemsProperty.type];

            const selectedArrayItemType = arrayItemTypes.filter((x) => x != 'null')[0];

            switch (selectedArrayItemType.toLowerCase()) {
                case 'string':
                    vType = ECpmCObjectStoreEntryProxyTypeApiEnum.STRING_ARRAY;
                    break;
                case 'boolean':
                    vType = ECpmCObjectStoreEntryProxyTypeApiEnum.BOOLEAN_ARRAY;
                    break;
                case 'integer':
                    vType = ECpmCObjectStoreEntryProxyTypeApiEnum.INTEGER_ARRAY;
                    break;
                case 'number':
                    vType = ECpmCObjectStoreEntryProxyTypeApiEnum.FLOAT_ARRAY;
                    break;
            }
        } else {
            switch (propertySelectedType.toLowerCase()) {
                case 'string':
                    vType = ECpmCObjectStoreEntryProxyTypeApiEnum.STRING;
                    break;
                case 'boolean':
                    vType = ECpmCObjectStoreEntryProxyTypeApiEnum.BOOLEAN;
                    break;
                case 'integer':
                    vType = ECpmCObjectStoreEntryProxyTypeApiEnum.INTEGER;
                    break;
                case 'number':
                    vType = ECpmCObjectStoreEntryProxyTypeApiEnum.FLOAT;
                    break;
            }
        }

        let schemaKey: string | undefined = undefined;
        if (
            vType == ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT ||
            vType == ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT_ARRAY
        ) {
            const sourceProperty =
                vType == ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT_ARRAY ? arrayItemsProperty : propertySchema;
            if (!sourceProperty?.$id) throw new Error('Unsuported case, where excepting an object schema');

            schemaKey = sourceProperty.$id.replace('.schema.json', '');
        }

        return {
            vtype: vType,
            schemaKey: schemaKey
        };
    }

    public static getRevertSubmitRequestMessages(
        key: string,
        historyIndex: number
    ): ISubmitRequestMessages<CpmCObjectStoreEntryProxyApiModel> {
        return {
            loading: `Reverting ${key} to v${historyIndex}...`,
            successSummary: `Reverted ${key}`,
            errorSummary: `Failed to revert ${key}`,
            successDetail: () => `Restored version v${historyIndex}`,
            errorDetail: (err: unknown) => (err as {error?: {message?: string}})?.error?.message ?? 'Unknown'
        };
    }
}
